import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { WindowRef } from '@helpers/windowref';
import { CanvasExtensionsService } from '@service/canvas/canvas-extensions.service';
import { CanvasInputMode, CanvasInputModeService } from '@service/canvas/canvas-input-mode.service';
import { CanvasService } from '@service/canvas/canvas.service';
import { SelectedElementsService } from '@service/canvas/selected-elements.service';
import { windowReferenceService } from '@service/canvas/window-reference.service';
import { ZoomService } from '@service/canvas/zoom.service';
import { EditorService, MenuTabs } from '@service/editor.service';
import { MimicsService } from '@service/mimics.service';
import { VesselService } from '@service/vessel.service';
import { Mimic } from 'icreate/common/models/mimic';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { Subject, firstValueFrom, lastValueFrom, take, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { KeyboardShortcutHandler, KeyboardShortcutService } from '@service/canvas/keyboard-shortcut.service';
import { LineSelectorHelperService } from '@service/canvas/line-selector-helper.service';
import { ElementAttribute, contextMenuItemList, hotkey, zoomMaxValue, zoomMinValue } from '@models/editor';
import { MoveCanvasService } from '@service/canvas/move-canvas.service';
import { AuthService } from '@service/auth.service';
import { PasteService } from '@service/canvas/paste.service';
import { UndoManagerService } from '@service/canvas/undoMgr.service';
import { TextModeHelperService } from '@service/canvas/text-mode-helper.service';
import { PathEditService } from '@service/canvas/path-editor.service';
import { EditorSideMenuComponent } from './editor-side-menu/editor-side-menu.component';
import { TriangleDrawHelperService } from '@service/canvas/triangle-draw-helper.service';
import { FreeDrawService } from '@service/canvas/free-draw-helper.service';
import { ConnectorPlacementService } from '@service/canvas/connector-placement.service';
import { ShipyardService } from '@service/shipyard.service';
import { Shipyard } from 'icreate/common/models/shipyard';
import { TranslateService } from '@ngx-translate/core';
import { LibraryService } from '@service/library.service';
import { Library, LibraryAndCategories } from 'icreate/common/models/library';
import { LibraryCategory } from 'icreate/common/models/library-category';
import { LibraryCategoryService } from '@service/library-category.service';
import { LibraryComponentService } from '@service/library-component.service';
import { LibraryComponent } from 'icreate/common/models/library-component';
import { SaveMimicDialogComponent } from '@components/save-mimic-dialog/save-mimic-dialog.component';
import { NewMimicDialogComponent } from '@components/editor/new-mimic-dialog/new-mimic-dialog.component';
import { MimicFolderViewData, RootItem, RootItemType } from 'icreate/common/models/mimic-folder';
import { MimicFolderService } from '@service/mimic-folder.service';
import { EditorSocketHelperService } from '@service/editor-socket-helper.service';
import { EditorHeaderComponent } from './editor-header/editor-header.component';
import { OrientationPopupConfig } from '@components/editor/orientation-popup/orientation-popup.component';
import { ElementSelectionService } from '@service/canvas/element-selection.service';
import { ElementOperationsService } from '@service/canvas/element-operations.service';
import { EditorMimicActionsService } from '@service/editor-mimic-actions.service';
import { VesselSocketService } from '@service/vessel-socket.service';
import { AutosaveControlService, AutosavePage } from '@service/auto-save-controller.service';
import { DuplicateMimicDialogComponent } from '@components/editor/duplicate-mimic-dialog/duplicate-mimic-dialog.component';
import { ButtonLabel, DuplicateItemType } from '@helpers/constants';
import { ElementSettingsService } from '@service/canvas/element-settings.service';
import { ChannelService } from '@service/channel/channel.service';
import { SystemColorService } from '@service/system-color.service';
import { GaugeExtensionService } from '@service/canvas/gauge-extension.service';

@Component({
    selector: 'app-editor',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.css'],
    standalone: false,
})
export class EditorComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly CONTEXTMENU_ITEM_LIST = contextMenuItemList;

    @ViewChild('workarea', { static: true }) workarea: ElementRef;
    @ViewChild('exitMimicConfirmation') exitMimicConfirmation: ConfirmationWindowComponent;
    @ViewChild('inputTextHolder') inputTextHolder: ElementRef;
    @ViewChild('showPreviewConfirmation') showPreviewConfirmation: ConfirmationWindowComponent;
    @ViewChild('editorContextMenu') editorContextMenu: ElementRef;
    @ViewChild('sideMenu') editorSideMenu: EditorSideMenuComponent;
    @ViewChild('deleteDialog') deleteDialog: ConfirmationWindowComponent;
    @ViewChild('headerComponent') headerComponent: EditorHeaderComponent;

    canvas: any;

    selectedMimic: Mimic;
    vessel: Vessel;
    rootList: RootItem[];
    libraryList: LibraryAndCategories[];
    initialSvgContent: string;
    contextMenuProvideSelectedElement: any[];

    plusCursor: boolean = false;
    isInCanvasMoveMode: boolean = false;
    isInDrawMode: boolean = false;
    contextMenuShow: boolean = false;
    userCanEdit: boolean = true;

    showLoadingSpinner: boolean = false;
    savedSuccessful: boolean = false;
    deleteMainMessage: string;
    deleteSubMessage: string;
    deleteCancelLabel: string;
    deleteConfirmLabel: string;
    deleteThirdLabel: string;
    deleteVariant: string = '';
    deleteFolderWithMimics: boolean = true;

    orientationPopupConfig: OrientationPopupConfig | null = null;

    private destroySubscriptionSubject$ = new Subject<void>();
    private hasCenteredCanvas = false;
    private ctrlPressed = false;

    constructor(
        private cdr: ChangeDetectorRef,
        private winRef: WindowRef,
        private authService: AuthService,
        private editorService: EditorService,
        private canvasService: CanvasService,
        private mimicService: MimicsService,
        private moveCanvas: MoveCanvasService,
        private zoomService: ZoomService,
        private windowElementRef: windowReferenceService,
        private route: ActivatedRoute,
        private router: Router,
        private vesselService: VesselService,
        private shipyardService: ShipyardService,
        private selectionService: SelectedElementsService,
        private canvasModeService: CanvasInputModeService,
        private extensions: CanvasExtensionsService,
        private inputModeService: CanvasInputModeService,
        private dialog: MatDialog,
        private keyboardShortcuts: KeyboardShortcutService,
        private lineSelectorHelperService: LineSelectorHelperService,
        private pasteSerivce: PasteService,
        private undoManagerService: UndoManagerService,
        private textModeHelperService: TextModeHelperService,
        private pathEditService: PathEditService,
        private triangleDrawHelperService: TriangleDrawHelperService,
        private freeDrawService: FreeDrawService,
        private translate: TranslateService,
        private libraryService: LibraryService,
        private translateService: TranslateService,
        private libraryCategoryService: LibraryCategoryService,
        private libraryComponentService: LibraryComponentService,
        private mimicFolderService: MimicFolderService,
        private editorSocketHelper: EditorSocketHelperService,
        private connectorPlacementService: ConnectorPlacementService,
        private elementSelectionService: ElementSelectionService,
        private elementOperationsService: ElementOperationsService,
        private mimicActionsService: EditorMimicActionsService,
        private vesselSocketService: VesselSocketService,
        private autosaveControlService: AutosaveControlService,
        private elementSettingsService: ElementSettingsService,
        private channelService: ChannelService,
        private systemColorService: SystemColorService,
        private gaugeExtension: GaugeExtensionService
    ) {
        this.handleSelectionChange = this.handleSelectionChange.bind(this);
    }

    @HostListener('window:beforeunload', ['$event'])
    async beforeUnload(event) {
        if (this.mimicHasChanged()) {
            event.preventDefault();
        }
    }

    @HostListener('window:copy', [])
    copySelected() {
        this.pasteSerivce.copyElements();
    }

    @HostListener('window:paste', [])
    async paste() {
        if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
            return;
        }
        await this.pasteSerivce.pasteCopiedElements();
        this.cdr.detectChanges();
    }

    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (event.key === 'Control') this.ctrlPressed = true;
    }

    @HostListener('window:keyup', ['$event'])
    onKeyUp(event: KeyboardEvent) {
        if (event.key === 'Control') this.ctrlPressed = false;
    }

    @HostListener('window:blur')
    onWindowBlur() {
        this.ctrlPressed = false;
    }

    @HostListener('wheel', ['$event'])
    handleMouseScroll(event: WheelEvent) {
        if (!event.ctrlKey && !event.metaKey) return;
        const isPinch = event.ctrlKey && !this.ctrlPressed && !event.metaKey;
        const sensitivity = isPinch ? 0.3 : 0.1;
        const dy = event.deltaY * -1;
        let nextVal = this.canvas.getZoom() * 100 + dy * this.canvas.getZoom() * sensitivity;
        nextVal = Math.max(zoomMinValue, nextVal);
        nextVal = Math.min(zoomMaxValue, nextVal);

        this.onZoomSlider(nextVal, { x: event.clientX, y: event.clientY });
        event.preventDefault();
    }

    @HostListener('document:contextmenu', ['$event'])
    @HostListener('document:click', ['$event'])
    editorContainerContextMenuClose(event: MouseEvent) {
        this.contextMenuClose();
        if (this.orientationPopupConfig) {
            const clickedInside = this.orientationPopupConfig.element.contains(event.target as Node);
            if (!clickedInside) {
                this.orientationPopupConfig = null;
            }
        }
    }

    ngOnInit(): void {
        this.editorService.editorLoadingEmitter
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(showSpinner => {
                this.showLoadingSpinner = showSpinner;
            });

        this.windowElementRef.init({
            workarea: this.workarea,
        });

        this.canvasService.init();
        this.canvas = this.canvasService.getCanvas();
        this.extensions.init();

        this.selectionService.init();
        this.moveCanvas.init();
        this.zoomService.init();
        this.keyboardShortcuts.init();
        this.lineSelectorHelperService.init();
        this.textModeHelperService.init();
        this.selectionService.onElementsSelected().subscribe(this.handleSelectionChange);

        this.extensions.installLegacyExtensions();
        this.extensions.installControlersExtensions();
        this.initKeyboardShortcuts();
        this.canvasModeService.init();
        this.pathEditService.init();
        this.triangleDrawHelperService.init();
        this.freeDrawService.init();
        this.connectorPlacementService.init();

        this.route.params.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(async params => {
            const vesselId = params['vesselId'];
            this.editorService.setCurrentMimic(undefined);
            const shipyardList: Shipyard[] = await this.shipyardService.getList();
            if (shipyardList.length == 0) {
                this.router.navigateByUrl('home');
                return;
            }

            const currentVessel: any = await this.vesselService.getVessel(vesselId);
            if (!currentVessel) {
                this.router.navigateByUrl('home');
                return;
            }
            this.vessel = currentVessel;
            this.vesselService.setActiveVessel(currentVessel);
            this.rootList = await this.mimicFolderService.getByVesselId(currentVessel.id);
            this.rootList = this.rootList.map(item => {
                if (item.type !== RootItemType.folder) return item;
                return {
                    ...item,
                    folder: {
                        ...item.folder,
                        expanded: false,
                    },
                };
            });
            const { nextMimic, rootList } = this.editorSocketHelper.findCurrentMimic(this.rootList);
            this.rootList = [...rootList];

            this.vessel = currentVessel;
            this.libraryList = await this.libraryService.getByShipyardId(this.vessel.shipyardId);
            this.libraryList = this.libraryList.map(library => {
                return {
                    ...library,
                    expanded: false,
                };
            });

            this.vesselService.accessUpdated$.pipe(take(1)).subscribe(canEdit => {
                this.updateUserEdit(canEdit);
            });

            let nextSelectedMimic = nextMimic;
            if (!nextMimic) {
                this.createNewMimic({ mimicFolderId: null, index: 0 }, false);
            } else {
                const state = history.state;
                const selectedMimicState = state?.selectedMimic;
                if (selectedMimicState) {
                    const mimicList =
                        this.rootList?.flatMap(item =>
                            item.type === RootItemType.mimic ? [item.mimic] : (item.folder?.mimicList ?? [])
                        ) ?? [];

                    const selectedMimic = mimicList.find(mimic => mimic.id == selectedMimicState);
                    if (selectedMimic) nextSelectedMimic = selectedMimic;
                    const { selectedMimic: _, ...updatedState } = state;
                    history.replaceState(updatedState, '');
                }
                this.editorService.setCurrentMimic(nextSelectedMimic);
            }

            this.editorSocketHelper.init({
                vessel: this.vessel,
                initialRootList: this.rootList,
                initialLibraryList: this.libraryList,
                initialSelectedMimic: nextSelectedMimic,
            });

            this.canvasModeService.setMode(CanvasInputMode.SELECT);
        });

        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(canEdit => {
            this.updateUserEdit(canEdit);
        });

        this.vesselSocketService
            .vesselUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async (updatedVessel: Vessel) => {
                if (this.vessel.id === updatedVessel.id) {
                    this.vessel = updatedVessel;
                    this.vesselService.setActiveVessel(this.vessel);
                    this.cdr.detectChanges();
                }
            });
        this.inputModeService
            .onModeChanged()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(mode => {
                this.plusCursor = false;
                if (this.inputModeService.showPlusCursor()) {
                    this.plusCursor = true;
                }
                this.isInCanvasMoveMode = mode === CanvasInputMode.MOVE_CANVAS;
                this.moveCanvas.setCanvasIsMoving(this.isInCanvasMoveMode);
                this.isInDrawMode = mode === CanvasInputMode.FREE_DRAW;
            });

        this.editorSocketHelper.libraryList$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(list => (this.libraryList = list));

        this.editorSocketHelper.rootList$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(list => {
            this.rootList = list;
        });

        this.dialog.afterOpened.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(() => {
            this.autosaveControlService.setDialogOpen(AutosavePage.MIMIC, true);
        });

        this.dialog.afterAllClosed.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(() => {
            this.autosaveControlService.setDialogOpen(AutosavePage.MIMIC, false);
        });
    }

    ngAfterViewInit(): void {
        this.editorService.currentMimic$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(async mimic => {
            this.authService.pagesAreLoading(true);
            if (mimic) {
                if (!this.hasCenteredCanvas) {
                    queueMicrotask(() => {
                        this.scrollCanvasIntoView();
                    });
                    this.hasCenteredCanvas = true;
                }
                this.selectedMimic = mimic;
                this.editorSocketHelper.setSelectedMimic(mimic);
                this.loadView(this.selectedMimic);
                await this.updateChannelElements(mimic);
                const serializedContent = this.mimicActionsService.serializeCurrentSvg();
                this.initialSvgContent = _.cloneDeep(serializedContent);
                this.editorService.setCurrentMimicIsDeleted(false);
                this.cdr.detectChanges();
            } else {
                this.selectedMimic = undefined;
                this.editorSocketHelper.setSelectedMimic(undefined);
                this.clearEditor();
                this.canvas.setSvgString('');
                const svgroot = this.canvas.svgroot;
                document.getElementById('editorContainer').append(svgroot);
                document.getElementById('svgcontent').innerHTML = '';
                const background = document.getElementById('canvasBackground');
                background.children[0].setAttribute('fill', 'white');
                background.setAttribute('width', '0');
                background.setAttribute('height', '0');
            }
            const serializedContent = this.mimicActionsService.serializeCurrentSvg();
            this.initialSvgContent = _.cloneDeep(serializedContent);
            this.cdr.detectChanges();
            const canvas: any = this.canvasService.getCanvas();
            canvas?.undoMgr?.resetUndoStack();
            this.authService.pagesAreLoading(false);
        });

        const mimics = [];

        this.editorService.updateMimicContent$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(mimicContent => {
                this.handleMimicSaved(mimicContent);
            });

        this.rootList?.forEach(item => {
            if (item.type === RootItemType.mimic) {
                mimics.push(item.mimic);
            } else if (item.type === RootItemType.folder) {
                mimics.push(...(item.folder?.mimicList ?? []));
            }
        });

        if (mimics.length > 0 && !this.selectedMimic) {
            this.deleteMainMessage = this.translate.instant('mimics.delete.dialog.main-label', {
                mimicName: mimics[0].name,
            });
            this.deleteSubMessage = 'mimics.delete.dialog.sub-label';
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
        this.triangleDrawHelperService.destroy();
        this.freeDrawService.destroy();
        this.connectorPlacementService.destroy();
        this.editorSocketHelper.destroy();
        this.orientationPopupConfig = null;
    }

    editorContainerContextMenu(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (
            this.contextMenuProvideSelectedElement &&
            this.contextMenuProvideSelectedElement.length !== 0 &&
            this.contextMenuProvideSelectedElement[0].nodeName === 'g'
        ) {
            if ((event.target as any).nodeName === 'rect') {
                this.contextMenuProvideSelectedElement = [event.target];
                this.selectionService.setSelectedElements(this.contextMenuProvideSelectedElement);
            }
        }
        const parentRect = (event.currentTarget as HTMLElement).getBoundingClientRect();

        const offsetX = event.clientX - parentRect.left;
        const offsetY = event.clientY - parentRect.top;

        this.editorContextMenu.nativeElement.style.top = `${offsetY}px`;
        this.editorContextMenu.nativeElement.style.left = `${offsetX}px`;
        this.editorContextMenu.nativeElement.style.display = 'flex';

        this.contextMenuShow = true;
    }

    contextMenuClose() {
        if (this.contextMenuShow) {
            this.editorContextMenu.nativeElement.style.display = 'none';
            this.contextMenuShow = false;
        }
    }

    setCurrentText(event) {
        this.canvas.setTextContent(event.currentTarget.value);
    }

    closeOrientationPopup() {
        this.orientationPopupConfig = null;
    }

    handleSelectionChange(elems) {
        if (!elems || elems.length === 0) {
            this.orientationPopupConfig = null;
            return;
        }
        this.contextMenuProvideSelectedElement = _.cloneDeep(elems);
        if (elems[0].tagName === 'text') {
            this.inputTextHolder.nativeElement.value = elems[0].textContent;
        }
        this.orientationPopupConfig = this.elementSelectionService.buildOrientationPopupConfig(elems);
    }

    private scrollCanvasIntoView() {
        document.getElementById('canvasBackground')?.scrollIntoView({
            block: 'center',
            inline: 'center',
        });
    }

    private loadView(mimic: Mimic) {
        if (mimic) {
            this.clearEditor();
            this.canvas.setResolution((mimic.profile.width - 50) * 0.8, (mimic.profile.height - 65) * 0.8);
            let svgcontent = mimic.svgcontent ? mimic.svgcontent : this.canvasService.getEmptySvg(mimic);
            if (this.canvas) {
                this.canvas.setSvgString(svgcontent);
                const svgroot = this.canvas.svgroot;
                svgroot.style.position = 'absolute';
                this.fixForeignObjects(svgroot);
                if (!document.getElementById('canvasBackground') && svgroot) {
                    document.getElementById('editorContainer').append(svgroot);
                    if (document.getElementById('canvasBackground')) {
                        document
                            .getElementById('canvasBackground')
                            .children[0].setAttribute('fill', mimic.profile.bkcolor);
                    } else {
                        return;
                    }
                } else {
                    document.getElementById('canvasBackground').children[0].setAttribute('fill', mimic.profile.bkcolor);
                }
            }

            this.zoomService.setZoom(this.zoomService.getZoom());
            this.scrollCanvasIntoView();
        }
    }

    private async updateChannelElements(mimic: Mimic) {
        try {
            const [elementsSettings, systemColors] = await Promise.all([
                this.elementSettingsService.getAllByMimicId(mimic.id),
                this.systemColorService.getAll(mimic.vesselId),
            ]);
            await Promise.all(
                elementsSettings
                    .filter(setting => setting.channelTag)
                    .map(async setting => {
                        const element = document.getElementById(setting.elementId);
                        const elementType = element?.getAttribute(ElementAttribute.type);
                        const iCreateType = element?.getAttribute(ElementAttribute.iCreateType);
                        const isValueElement =
                            elementType === CanvasInputMode.INPUT_VALUE || elementType === CanvasInputMode.OUTPUT_VALUE;
                        const isGaugeElement = iCreateType === CanvasInputMode.GAUGE;
                        if (!isValueElement && !isGaugeElement) return;
                        const channel = await this.channelService.getByRowId(mimic.vesselId, setting.channelTag);
                        if (!channel || this.selectedMimic?.id !== mimic.id) return;
                        if (isGaugeElement) {
                            this.gaugeExtension.render(element as unknown as SVGGElement, channel, systemColors);
                        } else if (isValueElement) {
                            this.extensions.updateInputUnit(element, channel.opsData?.measurementUnit);
                        }
                    })
            );
        } catch (err) {
            console.error(err);
        }
    }

    private fixForeignObjects(elem: Element) {
        const self = this;
        if (elem.nodeName.toLowerCase() == 'foreignobject') {
            this.extensions.attachEventListeners(elem);
        } else {
            elem.childNodes.forEach(function (child) {
                self.fixForeignObjects(<Element>child);
            });
        }
    }

    private clearEditor() {
        if (this.winRef.nativeWindow.svgEditor) {
            this.winRef.nativeWindow.svgEditor.clickClearAll();
        }
    }

    handleMimicSaved(svgContent) {
        const serializedContent = this.mimicActionsService.serializeCurrentSvg();
        this.selectedMimic.svgcontent = svgContent;
        this.initialSvgContent = _.cloneDeep(serializedContent);
    }

    async handleSaveMimicRequest() {
        const settingsMenu = this.editorSideMenu?.getSettingsMenuElement();
        if (settingsMenu?.hasChanges()) {
            await settingsMenu.saveSettings();
        }
        this.selectionService.setSelectedElementsForSaveButton([]);

        try {
            const serializedContent = this.mimicActionsService.serializeCurrentSvg();
            this.selectedMimic.svgcontent = serializedContent;
            const saveDialogRef = this.dialog.open(SaveMimicDialogComponent, {
                data: {
                    selectedMimic: this.selectedMimic,
                },
            });
            const savedSuccessful = await lastValueFrom(saveDialogRef.afterClosed());
            if (savedSuccessful) {
                this.savedSuccessful = true;
                setTimeout(() => {
                    this.savedSuccessful = false;
                }, 3000);
            }
        } catch (err) {
            console.error(err);
        }

        if (this.headerComponent) {
            this.headerComponent.updateSaveTime();
        }
    }

    async confirmUnsavedSettings(): Promise<boolean> {
        const settingsMenu = this.editorSideMenu?.getSettingsMenuElement();
        const confirmationDialog = this.editorSideMenu?.getExitSettingsConfirmation();

        if (!settingsMenu || !confirmationDialog) {
            return true;
        }

        if (!settingsMenu.hasChanges()) {
            this.editorService.setCurrentSelectedTab(MenuTabs.DESIGN);
            return true;
        }

        confirmationDialog.setVisible(true, true, AutosavePage.MIMIC);

        const option = await lastValueFrom(confirmationDialog.onOptionSelected.pipe(take(1)));

        if (option === ConfirmationWindowTypes.SAVE) {
            await settingsMenu.saveSettings();
            this.editorService.setCurrentSelectedTab(MenuTabs.DESIGN);
            return true;
        }

        if (option === ConfirmationWindowTypes.DISCARD) {
            this.editorService.setCurrentSelectedTab(MenuTabs.DESIGN);
            return true;
        }

        return false;
    }

    async confirmUnsavedDesign(): Promise<boolean> {
        if (!this.mimicHasChanged()) return true;

        this.exitMimicConfirmation.setVisible(true, true, AutosavePage.MIMIC);
        const option = await lastValueFrom(this.exitMimicConfirmation.onOptionSelected.pipe(take(1)));

        if (option === ConfirmationWindowTypes.SAVE) {
            await this.handleSaveMimicRequest();
            return true;
        }
        if (option === ConfirmationWindowTypes.DISCARD) {
            return true;
        }

        return false;
    }

    canCreateOrDelete(): boolean {
        return this.mimicActionsService.canCreateOrDelete(this.vessel, this.userCanEdit);
    }

    async createNewMimic(event: { mimicFolderId: string; index: number }, showPermission = true) {
        const { mimicFolderId, index } = event;
        if (!this.canCreateOrDelete()) {
            if (showPermission) this.showNoPermissionDialog();
            return;
        }
        try {
            if (!(await this.confirmUnsavedSettings())) return;
            if (!(await this.confirmUnsavedDesign())) return;

            const dialogRef = this.dialog.open(NewMimicDialogComponent, {
                width: '474px',
                data: { vessel: this.vessel, rootList: this.rootList, mimicFolderId: mimicFolderId },
                disableClose: true,
            });

            const mimic = (await lastValueFrom(dialogRef.componentInstance.onSaveButtonClick.pipe(take(1)))) as {
                name: string;
                userId: string;
                vesselId: string;
                bkcolor: string;
                color: string;
                grid: number;
                allowGrid: boolean;
            };

            if (mimic) {
                const profile = {
                    bkcolor: mimic.bkcolor,
                    color: mimic.color,
                    grid: mimic.grid,
                    allowGrid: mimic.allowGrid,
                    width: 1704,
                    height: 864,
                    margin: 10,
                };
                const emptySvg = this.canvasService.getEmptySvg({
                    name: mimic.name,
                    profile: profile,
                } as any);
                const newMimic = await this.mimicService.create(
                    mimic.name,
                    mimic.userId,
                    mimic.vesselId,
                    index,
                    mimicFolderId,
                    null,
                    profile,
                    emptySvg
                );
                if (newMimic) {
                    this.editorService.setCurrentMimic(newMimic);
                }
            }
        } catch (err) {
            console.error(err);
        }
    }

    async handleGoBack() {
        if (!(await this.confirmUnsavedSettings())) return;
        if (!(await this.confirmUnsavedDesign())) return;
        this.router.navigate([`/home/${this.vessel.shipyardId}/${this.vessel.id}`]);
    }

    async handleSelectMimic(mimic) {
        if (this.selectedMimic?.id === mimic?.id) return;
        if (!(await this.confirmUnsavedSettings())) return;
        if (!(await this.confirmUnsavedDesign())) return;
        this.editorService.setCurrentMimic(mimic);
    }

    async handleShowPreview() {
        if (!(await this.confirmUnsavedSettings())) return;
        if (!(await this.confirmUnsavedDesign())) return;
        this.winRef.nativeWindow.open(
            `editor/${this.vessel.id}/viewer/${this.selectedMimic?.id}`,
            'mimicViewer',
            'width=1274,height=933,menubar=0'
        );
    }

    showNoPermissionDialog() {
        this.mimicActionsService.showNoPermissionDialog();
    }

    async deleteMimic(event: { mimic: Mimic }) {
        if (!this.canCreateOrDelete()) {
            this.showNoPermissionDialog();
            return;
        }
        this.deleteMainMessage = this.translate.instant('mimics.delete.dialog.main-label', {
            mimicName: event.mimic.name,
        });
        this.deleteSubMessage = 'mimics.delete.dialog.sub-label';
        this.deleteCancelLabel = 'dlg.cancel';
        this.deleteThirdLabel = '';
        this.deleteConfirmLabel = 'dlg.confirm';
        this.deleteVariant = '';

        try {
            this.deleteDialog.setVisible(true, true, AutosavePage.MIMIC);
            const option = await lastValueFrom(this.deleteDialog.onOptionSelected.pipe(take(1)));
            this.authService.pagesAreLoading(true);
            if (option === ConfirmationWindowTypes.SAVE) {
                this.editorService.setCurrentMimicIsDeleted(true);
                await this.mimicService.delete(event.mimic.id);
            } else return;
        } catch (err) {
            console.error(err);
        } finally {
            this.authService.pagesAreLoading(false);
            this.editorService.setCurrentMimicIsDeleted(false);
        }
    }

    async duplicateMimic(mimic: Mimic) {
        if (!this.canCreateOrDelete()) {
            this.showNoPermissionDialog();
            return;
        }
        try {
            if (!(await this.confirmUnsavedSettings())) return;
            if (!(await this.confirmUnsavedDesign())) return;

            const containingFolder = this.rootList?.find(
                item => item.type === RootItemType.folder && item.folder?.mimicList?.some(m => m.id === mimic.id)
            );

            const mimicNames = containingFolder
                ? (containingFolder.folder?.mimicList?.map(m => m.name) ?? [])
                : (this.rootList
                      ?.filter(item => item.type === RootItemType.mimic)
                      .map(item => item.mimic?.name)
                      .filter((name): name is string => !!name) ?? []);

            const dialogRef = this.dialog.open(DuplicateMimicDialogComponent, {
                data: {
                    title: 'mimic.duplicate.duplicate-mimic.title',
                    initialName: mimic.name,
                    existingNames: mimicNames,
                    type: DuplicateItemType.MIMIC,
                    submitButtonLabel: ButtonLabel.DUPLICATE,
                    autoIncrementName: true,
                },
            });

            const result = await firstValueFrom(dialogRef.afterClosed());
            if (!result) return;

            const { name: mimicName } = result;
            const duplicatedMimic = { ...mimic, name: mimicName };

            this.authService.pagesAreLoading(true);
            await this.mimicActionsService.duplicateMimic(duplicatedMimic, () => this.canvas.getNextId());
        } catch (err) {
            console.error(err);
        } finally {
            this.authService.pagesAreLoading(false);
            this.editorService.setCurrentMimicIsDeleted(false);
        }
    }

    initKeyboardShortcuts() {
        const shortcuts: KeyboardShortcutHandler[] = [
            { key: 'delete', handleKeyDown: () => this.deleteSelected() },
            { key: 'backspace', handleKeyDown: () => this.deleteSelected() },
            { key: 'meta+z', handleKeyDown: () => this.undoManagerService.handleUndo() },
            { key: 'ctrl+z', handleKeyDown: () => this.undoManagerService.handleUndo() },
            { key: 'meta+y', handleKeyDown: () => this.undoManagerService.handleRedo() },
            { key: 'ctrl+y', handleKeyDown: () => this.undoManagerService.handleRedo() },
            { key: 'arrowright', handleKeyDown: () => this.moveSelectedElements(1, 0) },
            { key: 'arrowleft', handleKeyDown: () => this.moveSelectedElements(-1, 0) },
            { key: 'arrowup', handleKeyDown: () => this.moveSelectedElements(0, -1) },
            { key: 'arrowdown', handleKeyDown: () => this.moveSelectedElements(0, 1) },
            {
                key: ' ',
                handleKeyDown: () => this.moveCanvas.startMoveCanvas(),
                handleKeyUp: () => this.moveCanvas.stopMoveCanvas(),
            },
            { key: hotkey.front, handleKeyDown: () => this.moveFront() },
            { key: hotkey.back, handleKeyDown: () => this.moveBack() },
            { key: hotkey.group, handleKeyDown: () => this.canvas.groupSelectedElements() },
            { key: hotkey.ungroup, handleKeyDown: () => this.canvas.ungroupSelectedElement() },
            { key: 'h', handleKeyDown: () => this.canvasModeService.setMode(CanvasInputMode.MOVE_CANVAS) },
            { key: 'v', handleKeyDown: () => this.canvasModeService.setMode(CanvasInputMode.SELECT) },
        ];

        for (let i = 0; i < shortcuts.length; i++) {
            this.keyboardShortcuts.addShortcut(shortcuts[i]);
        }
    }

    deleteSelected = () => {
        this.elementOperationsService.deleteSelected(!!this.selectedMimic);
    };

    moveSelectedElements(dx, dy) {
        this.elementOperationsService.moveSelectedBy(dx, dy);
    }

    onZoomSlider(val, newCtr?) {
        const newVal = val / 100;
        this.zoomService.setZoom(newVal ?? this.canvas.getZoom(), newCtr);
    }

    private mimicHasChanged() {
        return this.mimicActionsService.mimicHasChanged(this.initialSvgContent);
    }

    async handleContextMenu(command: string, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.selectionService.setSelectedElements(this.contextMenuProvideSelectedElement);
        switch (command) {
            case hotkey.copy:
            case hotkey.copyInMac:
                this.copySelected();
                break;
            case hotkey.paste:
            case hotkey.pasteInMac:
                await this.paste();
                break;
            case hotkey.front:
                this.moveFront();
                break;
            case hotkey.back:
                this.moveBack();
                break;
            case hotkey.group:
                this.canvas.groupSelectedElements();
                break;
            case hotkey.ungroup:
                this.canvas.ungroupSelectedElement();
                break;
            default:
                console.log(`Unhandled key: ${command}`);
        }
        this.contextMenuClose();
    }

    moveFront() {
        this.elementOperationsService.moveSelectedToFront();
    }

    moveBack() {
        this.elementOperationsService.moveSelectedToBack();
    }

    lockedCanvasMouseEvent(event: MouseEvent) {
        this.elementSelectionService.handleLockedCanvasClick(event);
    }

    private async confirmAndDelete(
        entity: any,
        type: 'library' | 'category' | 'component' | 'folder',
        deleteFn: (id: string) => Promise<unknown>,
        deleteWithoutMimicsFn?: (id: string) => Promise<unknown>
    ): Promise<boolean> {
        try {
            this.deleteMainMessage = this.translateService.instant('editor.library.delete.main-title', {
                name: entity.name,
            });
            this.deleteSubMessage = `editor.library.delete.${type}.sub-message`;

            if (type === 'folder') {
                this.deleteCancelLabel = 'editor.library.delete.folder.button.cancel';
                this.deleteThirdLabel = 'editor.library.delete.folder.button.delete-folder';
                this.deleteConfirmLabel = 'editor.library.delete.folder.button.confirm';
                this.deleteVariant = 'folder-delete';
            } else {
                this.deleteCancelLabel = 'dlg.cancel';
                this.deleteThirdLabel = '';
                this.deleteConfirmLabel = 'dlg.confirm';
                this.deleteVariant = '';
            }

            this.deleteDialog.setVisible(true, true, AutosavePage.MIMIC);
            const option = await lastValueFrom(this.deleteDialog.onOptionSelected.pipe(take(1)));

            if (option === ConfirmationWindowTypes.SAVE) {
                this.showLoadingSpinner = true;
                await deleteFn(entity.id);
            } else if (option === ConfirmationWindowTypes.DELETE_FOLDER && deleteWithoutMimicsFn) {
                this.showLoadingSpinner = true;
                await deleteWithoutMimicsFn(entity.id);
            } else {
                return false;
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.showLoadingSpinner = false;
        }
    }

    async deleteLibrary(library: Library) {
        await this.confirmAndDelete({ id: library.id, name: library.name }, 'library', id =>
            this.libraryService.deleteLibrary(id)
        );
    }

    async deleteLibraryCategory(category: LibraryCategory) {
        await this.confirmAndDelete({ id: category.id, name: category.name }, 'category', id =>
            this.libraryCategoryService.deleteLibraryCategory(id)
        );
    }

    async deleteLibraryComponent(component: LibraryComponent) {
        await this.confirmAndDelete({ id: component.id, name: component.name }, 'component', id =>
            this.libraryComponentService.deleteComponent(id)
        );
    }

    async deleteMimicFolder(mimicFolder: MimicFolderViewData) {
        await this.confirmAndDelete(
            { id: mimicFolder.id, name: mimicFolder.name },
            'folder',
            id => this.mimicFolderService.deleteById(mimicFolder.id, mimicFolder.vesselId),
            id => this.mimicFolderService.deleteWithoutMimics(mimicFolder.id, mimicFolder.vesselId)
        );
    }

    openFolderExpanded(folderId: string) {
        this.rootList = this.rootList.map(item => {
            if (item.type !== RootItemType.folder) return item;
            if (item.folder.id !== folderId) return item;

            return {
                ...item,
                folder: {
                    ...item.folder,
                    expanded: !item.folder.expanded,
                },
            };
        });
        this.editorSocketHelper.setRootList(this.rootList);
    }

    openLibraryExpanded(libraryId: string) {
        this.libraryList = this.libraryList.map(lib => {
            if (lib.id !== libraryId) return lib;
            return {
                ...lib,
                expanded: !lib.expanded,
            };
        });
    }

    updateUserEdit(canEdit) {
        const currentVessel = this.vesselService.getActiveVessel();
        if (!currentVessel) return;
        this.userCanEdit = currentVessel.projectStatus === ProjectStatus.Editing && canEdit;
        if (this.userCanEdit) {
            this.workarea.nativeElement.removeEventListener('click', this.lockedCanvasMouseEvent.bind(this));
            this.canvas.setCanvasIsLocked(false);
        } else {
            this.workarea.nativeElement.addEventListener('click', this.lockedCanvasMouseEvent.bind(this));
            this.canvas.setCanvasIsLocked(true);
        }
        this.cdr.detectChanges();
    }
}
